import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export interface TeamMember {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'inactive';
  invited_at: string;
  joined_at: string | null;
  email?: string;
  full_name?: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: 'admin' | 'member';
  status: 'pending' | 'accepted' | 'expired';
  invited_by: string;
  created_at: string;
  expires_at: string;
}

export interface Team {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
}

export function useTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  const fetchTeamData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id, plan')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile?.team_id) {
        setTeam(null);
        setMembers([]);
        setInvitations([]);
        setLoading(false);
        return;
      }

      const { data: teamData } = await supabase
        .from('teams')
        .select('*')
        .eq('id', profile.team_id)
        .maybeSingle();

      if (teamData) {
        setTeam(teamData as Team);
        setIsOwner(teamData.owner_id === user.id);
      }

      const { data: membersData } = await supabase
        .from('team_members')
        .select('id, user_id, role, status, invited_at, joined_at')
        .eq('team_id', profile.team_id)
        .eq('status', 'active')
        .order('joined_at', { ascending: true });

      if (membersData) {
        // Fetch emails for members separately
        const memberIds = membersData.map((m: Record<string, unknown>) => m.user_id as string);
        const { data: memberProfiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', memberIds);

        const profileMap: Record<string, string> = {};
        if (memberProfiles) {
          memberProfiles.forEach((p: Record<string, unknown>) => {
            profileMap[p.id as string] = p.email as string || 'Unknown';
          });
        }

        const enrichedMembers = membersData.map((m: Record<string, unknown>) => ({
          ...m,
          email: profileMap[m.user_id as string] || 'Unknown',
          full_name: (profileMap[m.user_id as string] || 'User').split('@')[0],
        }));
        setMembers(enrichedMembers as TeamMember[]);
      }

      if (teamData?.owner_id === user.id) {
        const { data: invitesData } = await supabase
          .from('team_invitations')
          .select('*')
          .eq('team_id', profile.team_id)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .order('created_at', { ascending: false });

        if (invitesData) {
          setInvitations(invitesData as TeamInvitation[]);
        }
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const inviteMember = async (email: string, role: 'admin' | 'member' = 'member') => {
    if (!user || !team) throw new Error('No team found');

    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { error } = await supabase.from('team_invitations').insert({
      team_id: team.id,
      email: email.toLowerCase(),
      token,
      role,
      invited_by: user.id,
      expires_at: expiresAt.toISOString(),
    });

    if (error) throw error;

    await fetchTeamData();
    return token;
  };

  const removeMember = async (memberId: string) => {
    if (!user || !isOwner) throw new Error('Only team owners can remove members');

    const { error } = await supabase
      .from('team_members')
      .update({ status: 'inactive' })
      .eq('id', memberId);

    if (error) throw error;

    await fetchTeamData();
  };

  const cancelInvitation = async (invitationId: string) => {
    if (!user || !isOwner) throw new Error('Only team owners can cancel invitations');

    const { error } = await supabase
      .from('team_invitations')
      .update({ status: 'expired' })
      .eq('id', invitationId);

    if (error) throw error;

    await fetchTeamData();
  };

  const updateTeamName = async (newName: string) => {
    if (!user || !team || !isOwner) throw new Error('Only team owners can update team name');

    const { error } = await supabase
      .from('teams')
      .update({ name: newName, updated_at: new Date().toISOString() })
      .eq('id', team.id);

    if (error) throw error;

    await fetchTeamData();
  };

  return {
    team,
    members,
    invitations,
    loading,
    isOwner,
    inviteMember,
    removeMember,
    cancelInvitation,
    updateTeamName,
    refetch: fetchTeamData,
  };
}